// InterviewReportsPage — HR-facing comprehensive report viewer.
//
// Two modes (route-driven):
//   /jobs/:jobId/reports             → list every report for a job (grouped by round)
//   /jobs/:jobId/reports/:reportId   → detail (transcript + axis scores + narrative)

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronDown, ChevronRight,
  Download, Star, AlertTriangle, FileText, Mic, MessageSquare, Loader2,
  Users, TrendingUp, CheckCircle, XCircle, Clock, Bot, Shield, Award, Camera,
  Brain, Code2, Sparkles, Zap, Target, Activity, BarChart3, Phone,
} from "lucide-react";
import api from "../services/api";
import { toast } from "react-toastify";

const RECO_STYLE = {
  strong_hire:    { label: "Strong hire",    cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  hire:           { label: "Hire",           cls: "bg-emerald-50  text-emerald-700 border-emerald-200" },
  borderline:     { label: "Borderline",     cls: "bg-amber-50    text-amber-700   border-amber-200"  },
  no_hire:        { label: "No hire",        cls: "bg-rose-50     text-rose-700    border-rose-200"   },
  strong_no_hire: { label: "Strong no hire", cls: "bg-rose-100    text-rose-800    border-rose-300"   },
  time_expired:   { label: "Time expired",   cls: "bg-slate-100   text-slate-700   border-slate-300"  },
  no_response:    { label: "No response",    cls: "bg-slate-100   text-slate-600   border-slate-300"  },
};

const ROUND_LABEL = {
  preScreening:     "Pre-Screening",
  psychoanalytical: "Psychoanalytical",
  finalInterview:   "Final Interview",
  coding:           "Coding Test",
  gen_ai_readiness: "Gen AI Readiness",
};

// Visual accent colour per round
const ROUND_COLOR = {
  preScreening:     { bg: "bg-indigo-50",  border: "border-indigo-200",  pill: "bg-indigo-600",  text: "text-indigo-700"  },
  psychoanalytical: { bg: "bg-violet-50",  border: "border-violet-200",  pill: "bg-violet-600",  text: "text-violet-700"  },
  finalInterview:   { bg: "bg-orange-50",  border: "border-orange-200",  pill: "bg-orange-500",  text: "text-orange-700"  },
  coding:           { bg: "bg-cyan-50",    border: "border-cyan-200",    pill: "bg-cyan-600",    text: "text-cyan-700"    },
  gen_ai_readiness: { bg: "bg-sky-50",     border: "border-sky-200",     pill: "bg-sky-500",     text: "text-sky-700"     },
  default:          { bg: "bg-slate-50",   border: "border-slate-200",   pill: "bg-slate-500",   text: "text-slate-700"   },
};

function roundColor(round) {
  return ROUND_COLOR[round] || ROUND_COLOR.default;
}

// Per-round unique report configuration — icon, description, and named parameter labels.
// These labels map onto axisScores[].axis so the UI can highlight them distinctively.
const ROUND_CONFIG = {
  preScreening: {
    Icon: Phone,
    color: "indigo",
    title: "Pre-Screening Interview",
    description: "AI-conducted first-touch screen. Evaluates baseline communication, role fit signals, and red-flag detection before deeper assessment.",
    focusAreas: ["Communication Clarity", "Role Fit Signal", "Motivation & Intent", "Culture Alignment", "Articulation Quality"],
    uniqueLabel: "Screening Signals",
  },
  psychoanalytical: {
    Icon: Brain,
    color: "violet",
    title: "Behavioral & Psychoanalytical Interview",
    description: "Structured behavioral interview probing cognitive flexibility, emotional regulation, interpersonal dynamics, and stress response patterns.",
    focusAreas: ["Emotional Intelligence", "Behavioral Consistency", "Self-Awareness", "Conflict Resolution", "Adaptability"],
    uniqueLabel: "Behavioral Patterns",
  },
  finalInterview: {
    Icon: Award,
    color: "orange",
    title: "Final Interview",
    description: "Comprehensive deep-dive evaluating technical depth, strategic thinking, leadership potential, and overall hiring readiness.",
    focusAreas: ["Technical Depth", "Problem Solving", "Leadership Potential", "Strategic Thinking", "Decision Quality"],
    uniqueLabel: "Hiring Decision Factors",
  },
  coding: {
    Icon: Code2,
    color: "cyan",
    title: "Live Coding Interview",
    description: "Real-time coding session assessing algorithm design, code quality, debugging approach, and engineering reasoning.",
    focusAreas: ["Algorithm Design", "Code Quality", "Problem Decomposition", "Debugging Approach", "Edge Case Handling"],
    uniqueLabel: "Coding Performance",
  },
  gen_ai_readiness: {
    Icon: Sparkles,
    color: "sky",
    title: "Gen AI Readiness Assessment",
    description: "Evaluates practical AI tool fluency, prompt engineering ability, automation mindset, and ethical AI awareness.",
    focusAreas: ["AI Tool Fluency", "Prompt Engineering", "Automation Thinking", "AI Ethics Awareness", "Adoption Velocity"],
    uniqueLabel: "AI Readiness Indicators",
  },
};

const COLOR_MAP = {
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", icon: "text-indigo-600", badge: "bg-indigo-100 text-indigo-700", bar: "bg-indigo-500", heading: "text-indigo-800" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", icon: "text-violet-600", badge: "bg-violet-100 text-violet-700", bar: "bg-violet-500", heading: "text-violet-800" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600", badge: "bg-orange-100 text-orange-700", bar: "bg-orange-500", heading: "text-orange-800" },
  cyan:   { bg: "bg-cyan-50",   border: "border-cyan-200",   icon: "text-cyan-600",   badge: "bg-cyan-100 text-cyan-700",   bar: "bg-cyan-500",   heading: "text-cyan-800"   },
  sky:    { bg: "bg-sky-50",    border: "border-sky-200",    icon: "text-sky-600",    badge: "bg-sky-100 text-sky-700",    bar: "bg-sky-500",    heading: "text-sky-800"    },
};

function RoundHero({ report }) {
  const cfg = ROUND_CONFIG[report.round];
  if (!cfg) return null;
  const cl = COLOR_MAP[cfg.color] || COLOR_MAP.indigo;
  const Icon = cfg.Icon;

  // Separate axis scores that match a focus area from generic axes
  const focusScores = (report.axisScores || []).filter(a =>
    cfg.focusAreas.some(f => a.axis?.toLowerCase().includes(f.split(" ")[0].toLowerCase()))
  );
  // Fall back to first 3 axis scores if none matched by name
  const displayScores = focusScores.length > 0 ? focusScores : (report.axisScores || []).slice(0, 3);

  return (
    <div className={`${cl.bg} ${cl.border} border rounded-xl p-5 mb-6`}>
      {/* Type identity */}
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cl.bg} border ${cl.border} shrink-0`}>
          <Icon className={`w-5 h-5 ${cl.icon}`} />
        </div>
        <div className="flex-1">
          <div className={`text-xs font-bold uppercase tracking-widest mb-0.5 ${cl.icon}`}>{cfg.title}</div>
          <p className="text-sm text-slate-600 leading-relaxed">{cfg.description}</p>
        </div>
      </div>

      {/* Focus areas as tags */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {cfg.focusAreas.map(f => (
          <span key={f} className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cl.badge}`}>{f}</span>
        ))}
      </div>

      {/* Top axis scores inline for quick scan */}
      {displayScores.length > 0 && (
        <div>
          <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${cl.heading}`}>{cfg.uniqueLabel}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {displayScores.map((a, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-white/80 shadow-sm">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-slate-700 truncate pr-2">{a.axis}</span>
                  <span className={`text-sm font-black ${cl.icon}`}>{a.score ?? "—"}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div className={`${cl.bar} h-1.5 rounded-full transition-all`} style={{ width: `${a.score ?? 0}%` }} />
                </div>
                {a.note && <p className="text-[10px] text-slate-400 mt-1 line-clamp-2">{a.note}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ reco }) {
  const s = RECO_STYLE[reco] || RECO_STYLE.borderline;
  return <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${s.cls}`}>{s.label}</span>;
}

function ScoreChip({ value }) {
  if (value == null) return <span className="text-slate-400">—</span>;
  const v = Math.max(0, Math.min(100, value));
  const cls = v >= 75 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
             : v >= 50 ? "text-amber-700 bg-amber-50 border-amber-200"
             :           "text-rose-700 bg-rose-50 border-rose-200";
  return <span className={`px-2 py-0.5 text-xs font-bold rounded border ${cls}`}>{v}</span>;
}

function ScoreBar({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="w-full h-2 bg-slate-100 rounded">
      <div className={`h-2 rounded ${color}`} style={{ width: `${v}%` }} />
    </div>
  );
}

// ── Stat pill shown in the group header ────────────────────────────────
function StatPill({ icon: Icon, label, value, cls }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}:</span>
      <span>{value}</span>
    </div>
  );
}

// ── Total AI cost banner (shown above groups) ───────────────────────
function CostBanner({ jobId, reports }) {
  const [jobCost, setJobCost] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    api.get(`/reports/job/${jobId}/total-cost`)
      .then(r => setJobCost(r.data?.data || null))
      .catch(() => {});
  }, [jobId]);

  const fallbackUsd    = reports.reduce((s, r) => s + (r.cost?.usd    || 0), 0);
  const fallbackTokens = reports.reduce((s, r) => s + (r.cost?.tokens || 0), 0);
  const totalUsd       = jobCost ? jobCost.totalUsd    : fallbackUsd;
  const totalTokens    = jobCost ? jobCost.totalTokens : fallbackTokens;
  const candidates     = jobCost ? jobCost.candidateCount : new Set(reports.map(r => r.resumeId || r.candidateEmail)).size;
  const byStage        = jobCost?.byStage || [];
  const perCandidate   = candidates > 0 ? totalUsd / candidates : 0;

  if (totalUsd === 0 && totalTokens === 0) return null;

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 rounded-xl px-5 py-4 mb-5">
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indigo-400 font-semibold">Total AI Cost (all operations)</div>
            <div className="text-lg font-black text-indigo-700">${totalUsd.toFixed(4)}</div>
          </div>
        </div>
        <div className="h-8 w-px bg-indigo-100" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Tokens consumed</div>
          <div className="text-sm font-bold text-slate-700">{totalTokens.toLocaleString()}</div>
        </div>
        <div className="h-8 w-px bg-indigo-100" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Avg per candidate</div>
          <div className="text-sm font-bold text-slate-700">${perCandidate.toFixed(4)}</div>
        </div>
        <div className="h-8 w-px bg-indigo-100" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Candidates</div>
          <div className="text-sm font-bold text-slate-700">{candidates}</div>
        </div>
        {byStage.length > 0 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="ml-auto text-xs text-indigo-500 hover:text-indigo-700 font-semibold flex items-center gap-1"
          >
            {expanded ? "Hide" : "View"} breakdown
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {expanded && byStage.length > 0 && (
        <div className="mt-4 border-t border-indigo-100 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-indigo-400 font-semibold mb-2">
            Cost breakdown — JD generation · Resume screening · Interviews · Assessments · Offer letters
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-400 uppercase tracking-wide border-b border-indigo-100">
                <th className="pb-1.5 font-semibold">Stage / Operation</th>
                <th className="pb-1.5 font-semibold text-right">Ops</th>
                <th className="pb-1.5 font-semibold text-right">Tokens</th>
                <th className="pb-1.5 font-semibold text-right">Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {byStage.map((s, i) => (
                <tr key={i} className="border-b border-indigo-50">
                  <td className="py-1.5 font-medium text-slate-700">{s.stage}</td>
                  <td className="py-1.5 text-right text-slate-500">{s.count}</td>
                  <td className="py-1.5 text-right text-slate-500">{s.tokens.toLocaleString()}</td>
                  <td className="py-1.5 text-right font-semibold text-indigo-600">${s.usdCost.toFixed(4)}</td>
                </tr>
              ))}
              <tr className="bg-indigo-50/60 font-bold">
                <td className="py-1.5 text-indigo-700">Total</td>
                <td className="py-1.5 text-right text-indigo-700">{byStage.reduce((s,e)=>s+e.count,0)}</td>
                <td className="py-1.5 text-right text-indigo-700">{totalTokens.toLocaleString()}</td>
                <td className="py-1.5 text-right text-indigo-700">${totalUsd.toFixed(4)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Single collapsible group ───────────────────────────────────────────
function RoundGroup({ round, label, reports, jobId, nav, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const c = roundColor(round);

  // Summary metrics
  const count      = reports.length;
  const scored     = reports.filter(r => r.overallScore != null);
  const avgScore   = scored.length
    ? Math.round(scored.reduce((s, r) => s + r.overallScore, 0) / scored.length)
    : null;
  const hires      = reports.filter(r => r.recommendation === "hire" || r.recommendation === "strong_hire").length;
  const hireRate   = Math.round((hires / count) * 100);
  const recoGroups = {};
  reports.forEach(r => { recoGroups[r.recommendation] = (recoGroups[r.recommendation] || 0) + 1; });
  const roundUsd   = reports.reduce((s, r) => s + (r.cost?.usd || 0), 0);

  const roundLabel = (label || ROUND_LABEL[round] || round).replace(/\s*\(Arya\)/gi, '');

  return (
    <div className={`border ${c.border} rounded-xl overflow-hidden mb-4`}>
      {/* ── Header (always visible) ── */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-5 py-4 ${c.bg} hover:brightness-95 transition-all`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${c.pill}`} />
          <span className={`text-base font-bold ${c.text}`}>{roundLabel}</span>
          <span className="ml-1 px-2 py-0.5 rounded-full bg-white/70 border border-white text-xs font-semibold text-slate-600">
            {count} report{count !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {avgScore != null && (
            <StatPill icon={TrendingUp} label="Avg score" value={avgScore}
              cls="bg-white/80 border-white text-slate-600" />
          )}
          <StatPill icon={CheckCircle} label="Hire rate" value={`${hireRate}%`}
            cls={hireRate >= 50 ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                : "bg-rose-50 border-rose-200 text-rose-600"} />
          <StatPill icon={Users} label="Candidates" value={count}
            cls="bg-white/80 border-white text-slate-600" />
          {roundUsd > 0 && (
            <StatPill icon={TrendingUp} label="AI cost" value={`$${roundUsd.toFixed(4)}`}
              cls="bg-indigo-50 border-indigo-200 text-indigo-700" />
          )}
          {open
            ? <ChevronDown className="w-4 h-4 text-slate-400 ml-1" />
            : <ChevronRight className="w-4 h-4 text-slate-400 ml-1" />}
        </div>
      </button>

      {/* ── Recommendation bar (always visible under header) ── */}
      <div className="flex gap-2 px-5 py-2 bg-white border-t border-slate-100 flex-wrap">
        {Object.entries(recoGroups).map(([reco, cnt]) => {
          const s = RECO_STYLE[reco] || RECO_STYLE.borderline;
          return (
            <span key={reco} className={`px-2 py-0.5 text-xs rounded border ${s.cls}`}>
              {s.label} <strong>{cnt}</strong>
            </span>
          );
        })}
      </div>

      {/* ── Collapsible candidate table ── */}
      {open && (
        <div className="overflow-x-auto border-t border-slate-100">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Candidate</th>
                <th className="text-left px-4 py-3">Modality</th>
                <th className="text-left px-4 py-3">Score</th>
                <th className="text-left px-4 py-3">Recommendation</th>
                <th className="text-left px-4 py-3">Cost</th>
                <th className="text-left px-4 py-3">Completed</th>
              </tr>
            </thead>
            <tbody>
              {reports
                .slice()
                .sort((a, b) => (b.overallScore ?? -1) - (a.overallScore ?? -1))
                .map(r => (
                  <tr
                    key={r._id}
                    onClick={() => nav(`/jobs/${jobId}/reports/${r._id}`)}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 text-sm">{r.candidateName || "—"}</div>
                      <div className="text-xs text-slate-400">{r.candidateEmail || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.modality === "audio"
                        ? <span className="flex items-center gap-1 text-xs"><Mic className="w-3.5 h-3.5" />Audio</span>
                        : <span className="flex items-center gap-1 text-xs"><MessageSquare className="w-3.5 h-3.5" />Text</span>}
                    </td>
                    <td className="px-4 py-3"><ScoreChip value={r.overallScore} /></td>
                    <td className="px-4 py-3"><Badge reco={r.recommendation} /></td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      ${(r.cost?.usd || 0).toFixed(4)} · {r.cost?.tokens || 0} tk
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {new Date(r.completedAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Assessment (exam) group ────────────────────────────────────────────
function AssessmentGroup({ group, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [expandedRow, setExpandedRow] = useState(null);
  const { results = [] } = group;

  const passed     = results.filter(r => r.status === 'shortlisted').length;
  const avgScore   = results.length
    ? Math.round(results.reduce((s, r) => s + (r.score ?? 0), 0) / results.length)
    : null;
  const totalCost  = results.reduce((s, r) => s + (r.cost?.usd || 0), 0);
  const passRate   = Math.round((passed / results.length) * 100);

  const riskCls = { low: 'text-green-600 bg-green-50', medium: 'text-amber-600 bg-amber-50', high: 'text-red-600 bg-red-50' };

  return (
    <div className="border border-orange-200 rounded-xl overflow-hidden mb-4">
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-orange-50 hover:brightness-95 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
          <span className="text-base font-bold text-orange-700">{group.label}</span>
          <span className="ml-1 px-2 py-0.5 rounded-full bg-white/70 border border-white text-xs font-semibold text-slate-600">
            {results.length} report{results.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {avgScore != null && (
            <StatPill icon={Award} label="Avg score" value={`${avgScore}%`} cls="bg-white/80 border-white text-slate-600" />
          )}
          <StatPill icon={CheckCircle} label="Pass rate" value={`${passRate}%`}
            cls={passRate >= 50 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-600'} />
          <StatPill icon={Users} label="Candidates" value={results.length} cls="bg-white/80 border-white text-slate-600" />
          {totalCost > 0 && (
            <StatPill icon={TrendingUp} label="AI cost" value={`$${totalCost.toFixed(4)}`} cls="bg-indigo-50 border-indigo-200 text-indigo-700" />
          )}
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 ml-1" /> : <ChevronRight className="w-4 h-4 text-slate-400 ml-1" />}
        </div>
      </button>

      {/* Pass/fail bar */}
      <div className="flex gap-2 px-5 py-2 bg-white border-t border-slate-100 flex-wrap">
        <span className="px-2 py-0.5 text-xs rounded border bg-emerald-50 text-emerald-700 border-emerald-200">Passed <strong>{passed}</strong></span>
        <span className="px-2 py-0.5 text-xs rounded border bg-rose-50 text-rose-600 border-rose-200">Failed <strong>{results.length - passed}</strong></span>
      </div>

      {open && (
        <div className="border-t border-slate-100">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Candidate</th>
                <th className="text-left px-4 py-3">Score</th>
                <th className="text-left px-4 py-3">Result</th>
                <th className="text-left px-4 py-3">Proctoring</th>
                <th className="text-left px-4 py-3">Violations</th>
                <th className="text-left px-4 py-3">Cost</th>
                <th className="text-left px-4 py-3">Completed</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {results.slice().sort((a, b) => (b.score ?? -1) - (a.score ?? -1)).map(r => {
                const ed      = r.examDetails || {};
                const pass    = r.status === 'shortlisted';
                const rowOpen = expandedRow === r.resumeId + r.stageId;
                return (
                  <React.Fragment key={r.resumeId + r.stageId}>
                    <tr className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 text-sm">{r.candidateName}</div>
                        <div className="text-xs text-slate-400">{r.candidateEmail}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded border ${pass ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                          {r.score ?? '—'}% / {r.passingScore}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${pass ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-rose-100 text-rose-800 border-rose-300'}`}>
                          {pass ? '✓ Passed' : '✗ Failed'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {ed.proctoringScore != null ? (
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded ${riskCls[ed.riskLevel] || 'text-slate-500 bg-slate-50'}`}>
                            {ed.proctoringScore}/100 · {ed.riskLevel || '—'}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {ed.violationCount != null ? (
                          <span className={ed.violationCount > 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}>
                            {ed.violationCount}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        ${(r.cost?.usd || 0).toFixed(4)} · {r.cost?.tokens || 0} tk
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedRow(rowOpen ? null : r.resumeId + r.stageId)}
                          className="text-xs text-orange-500 hover:underline font-medium"
                        >
                          {rowOpen ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {rowOpen && (
                      <tr className="bg-slate-50 border-t border-slate-100">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-white border border-slate-100 rounded-lg p-3 text-center">
                              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Earned pts</div>
                              <div className="text-lg font-black text-slate-700">{ed.earnedPoints ?? '—'} / {ed.totalPoints ?? '—'}</div>
                            </div>
                            <div className="bg-white border border-slate-100 rounded-lg p-3 text-center">
                              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Time taken</div>
                              <div className="text-lg font-black text-slate-700">{ed.timeTaken ? `${Math.round(ed.timeTaken / 60)}m ${ed.timeTaken % 60}s` : '—'}</div>
                            </div>
                            <div className="bg-white border border-slate-100 rounded-lg p-3 text-center">
                              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Integrity</div>
                              <div className={`text-base font-black capitalize ${ed.integrityStatus === 'clean' ? 'text-green-700' : ed.integrityStatus === 'suspicious' ? 'text-amber-600' : 'text-rose-700'}`}>
                                {ed.integrityStatus || '—'}
                              </div>
                            </div>
                            <div className="bg-white border border-slate-100 rounded-lg p-3 text-center">
                              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Integrity score</div>
                              <div className="text-lg font-black text-slate-700">{ed.integrityScore ?? '—'}/100</div>
                            </div>
                          </div>

                          {/* Violations */}
                          {(ed.violations || []).length > 0 && (
                            <div className="mb-4">
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Violations ({ed.violations.length})</div>
                              <div className="space-y-1 max-h-36 overflow-y-auto">
                                {ed.violations.map((v, i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs bg-rose-50 border border-rose-100 rounded px-3 py-1.5">
                                    <span className="text-rose-500">●</span>
                                    <span className="font-medium text-rose-700 capitalize">{(v.type || '').replace(/_/g, ' ')}</span>
                                    <span className="text-slate-500">— {v.description}</span>
                                    <span className="ml-auto text-slate-400">{v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ''}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Graded answers */}
                          {(ed.gradedAnswers || []).length > 0 && (
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Graded Answers ({ed.gradedAnswers.length})</div>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {ed.gradedAnswers.map((a, i) => (
                                  <div key={i} className={`text-xs border rounded-lg px-3 py-2 ${a.correct ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-slate-700">Q{i + 1} · {(a.questionType || 'mcq').replace(/_/g, ' ')}</span>
                                      <span className={`font-bold ${a.correct ? 'text-emerald-700' : 'text-rose-700'}`}>
                                        {a.earnedPoints ?? 0}/{a.points ?? 10} pts
                                      </span>
                                    </div>
                                    {a.feedback && <div className="text-slate-600 italic">{a.feedback}</div>}
                                    {a.writtenAnswer && <div className="text-slate-500 mt-1">Answer: {a.writtenAnswer}</div>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  List view  (grouped by round)
// ─────────────────────────────────────────────────────────────────────
export function JobReportsList() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [reports, setReports]         = useState([]);
  const [assessments, setAssessments] = useState([]); // exam assessment groups
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [rRes, aRes] = await Promise.all([
          api.get(`/reports/job/${jobId}`),
          api.get(`/reports/job/${jobId}/assessments`).catch(() => ({ data: { data: [] } })),
        ]);
        setReports(rRes.data?.data || []);
        setAssessments(aRes.data?.data || []);
      } catch (e) {
        toast.error("Failed to load reports");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  // Group interview reports by stageId (when present) or round.
  // This ensures custom pipeline stages like SJT (Arya) get their own group
  // instead of being merged into the generic finalInterview group.
  const ROUND_ORDER = [
    "preScreening", "psychoanalytical", "coding", "gen_ai_readiness", "finalInterview",
  ];
  const DEFAULT_LABEL_FOR_ROUND = {
    preScreening:     "Pre-Screening Interview",
    psychoanalytical: "Psychoanalytical Assessment",
    finalInterview:   "Final Interview",
    coding:           "Coding Test",
    gen_ai_readiness: "Gen AI Readiness Interview",
  };

  // Reverse map: label → round, to resolve old reports that are missing the round field
  const LABEL_TO_ROUND = useMemo(() => Object.fromEntries(
    Object.entries(DEFAULT_LABEL_FOR_ROUND).map(([k, v]) => [v.toLowerCase(), k])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);

  const groups = useMemo(() => {
    const map = {};
    reports.forEach(r => {
      // Resolve round: use stored round, or infer from stageLabel for legacy reports without round
      const round = r.round ||
        (r.stageLabel && LABEL_TO_ROUND[r.stageLabel.toLowerCase()]) ||
        null;

      // Use stageId as key only when the stage has a custom label that differs
      // from the standard round label (e.g. SJT under finalInterview engine).
      // Otherwise group by round so all reports for the same stage merge.
      const isCustomLabel = r.stageId && !r.stageId.startsWith("legacy-") &&
        r.stageLabel && round && DEFAULT_LABEL_FOR_ROUND[round] &&
        r.stageLabel.toLowerCase() !== DEFAULT_LABEL_FOR_ROUND[round].toLowerCase();
      const key = isCustomLabel ? r.stageId : (round || "other");
      if (!map[key]) map[key] = { round: round || "other", label: r.stageLabel || null, reports: [] };
      map[key].reports.push(r);
    });
    // Sort: known rounds first in ROUND_ORDER, then custom stages alphabetically
    const known  = ROUND_ORDER.filter(k => map[k]);
    const custom = Object.keys(map).filter(k => !ROUND_ORDER.includes(k)).sort();
    return [...known, ...custom].map(k => ({ round: map[k].round, label: map[k].label, key: k, reports: map[k].reports }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const totalReports     = reports.length + assessments.reduce((s, g) => s + g.results.length, 0);
  const totalCandidates  = new Set([
    ...reports.map(r => r.resumeId || r.candidateEmail),
    ...assessments.flatMap(g => g.results.map(r => String(r.resumeId))),
  ]).size;
  const totalStages      = groups.length + assessments.length;
  const isEmpty          = reports.length === 0 && assessments.length === 0;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* ── Top bar ── */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => nav(-1)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 font-medium text-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <span className="text-slate-300">|</span>
          <h1 className="text-2xl font-bold text-slate-900">Assessment Reports</h1>
        </div>
        {!loading && totalReports > 0 && (
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" /> {totalReports} report{totalReports !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" /> {totalCandidates} candidate{totalCandidates !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1">
              <Bot className="w-4 h-4" /> {totalStages} stage{totalStages !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center p-12 text-slate-500 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading reports…
        </div>
      )}

      {!loading && isEmpty && (
        <div className="bg-white border border-slate-200 rounded-xl p-14 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No reports yet</p>
          <p className="text-slate-400 text-sm mt-1">
            Reports appear here as candidates complete their interview rounds.
          </p>
        </div>
      )}

      {!loading && !isEmpty && (
        <div>
          <CostBanner jobId={jobId} reports={reports} />

          {/* ── Interview / AI report groups ── */}
          {groups.map((g, i) => (
            <RoundGroup
              key={g.key || g.round}
              round={g.round}
              label={g.label}
              reports={g.reports}
              jobId={jobId}
              nav={nav}
              defaultOpen={i === 0}
            />
          ))}

          {/* ── Exam assessment groups ── */}
          {assessments.length > 0 && (
            <>
              {groups.length > 0 && (
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-400 px-2">Exam Assessments</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              )}
              {assessments.map((g, i) => (
                <AssessmentGroup key={g.stageId} group={g} defaultOpen={i === 0 && groups.length === 0} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Detail view
// ─────────────────────────────────────────────────────────────────────
export function InterviewReportDetail() {
  const { jobId, reportId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/reports/${reportId}`);
        setReport(r.data?.data || null);
      } catch (e) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId]);

  const printReport = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-500 gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!report) {
    return <div className="p-12 text-center text-slate-500">Report not found.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 print:p-0">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={printReport} className="flex items-center gap-2 px-3 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 text-sm">
          <Download className="w-4 h-4" /> Export PDF
        </button>
      </div>

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
              {(report.stageLabel || ROUND_LABEL[report.round] || report.round).replace(/\s*\(Arya\)/gi, '')} · {report.jobTitle}
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{report.candidateName}</h1>
            <div className="text-sm text-slate-500">{report.candidateEmail}</div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-3 justify-end mb-2">
              <Badge reco={report.recommendation} />
              <span className="text-3xl font-bold text-slate-900">{report.overallScore ?? "—"}</span>
              <span className="text-slate-500 text-sm">/100</span>
            </div>
            <div className="text-xs text-slate-500">
              {report.modality === "audio" ? "Audio interview" : "Text interview"} ·{" "}
              {new Date(report.completedAt).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Round-specific hero — unique parameters per assessment type */}
      <RoundHero report={report} />

      {/* No-response warning banner */}
      {report.recommendation === "no_response" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800 text-sm">Candidate did not respond</div>
            <div className="text-amber-700 text-sm mt-0.5">
              The candidate connected to the interview but provided no verbal responses. No AI score has been assigned.
              Please follow up with the candidate or re-invite them to complete the interview.
            </div>
          </div>
        </div>
      )}

      {/* Axis scores */}
      {report.axisScores?.length > 0 && (
        <Section title="Axis scores">
          <div className="space-y-3">
            {report.axisScores.map((a, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-slate-800">{a.axis}</div>
                  <div className="text-sm font-semibold text-slate-900">{a.score ?? "—"}</div>
                </div>
                <ScoreBar value={a.score} />
                {a.note && <div className="text-xs text-slate-500 mt-1">{a.note}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Strengths / red flags */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Section title="Strengths" icon={Star} iconCls="text-emerald-500">
          {report.strengths?.length ? (
            <ul className="space-y-2">
              {report.strengths.map((s, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-emerald-500">✓</span><span>{s}</span></li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-500">None recorded.</div>}
        </Section>

        <Section title="Red flags" icon={AlertTriangle} iconCls="text-rose-500">
          {report.redFlags?.length ? (
            <ul className="space-y-2">
              {report.redFlags.map((s, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-rose-500">!</span><span>{s}</span></li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-500">None recorded.</div>}
        </Section>
      </div>

      {/* Selection narrative */}
      {report.selectionNarrative && (
        <Section title="Selection narrative">
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{report.selectionNarrative}</p>
        </Section>
      )}

      {/* AI Cost */}
      <Section title="AI Cost Incurred" icon={TrendingUp} iconCls="text-indigo-500">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-indigo-400 mb-1">Interview cost</div>
            <div className="text-xl font-black text-indigo-700">${(report.cost?.usd || 0).toFixed(4)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Tokens used</div>
            <div className="text-xl font-black text-slate-700">{(report.cost?.tokens || 0).toLocaleString()}</div>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Duration</div>
            <div className="text-xl font-black text-slate-700">
              {report.durationSec ? `${Math.round(report.durationSec / 60)}m` : "—"}
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Modality</div>
            <div className="text-base font-bold text-slate-700 capitalize">{report.modality || "audio"}</div>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          Cost shown is for this interview round only. View the candidate's full report for total AI spend across all stages.
        </p>
      </Section>

      {/* Proctoring Report — shown 10 min after interview completes */}
      <ProctoringSection proctoring={report.proctoring} completedAt={report.completedAt} />

      {/* Full transcript */}
      <Section title="Full transcript" icon={FileText} iconCls="text-slate-500">
        {report.transcript?.length ? (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto print:max-h-none">
            {report.transcript.map((t, i) => (
              <div key={i} className={`flex ${t.role === "candidate" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                  t.role === "candidate" ? "bg-blue-50 text-slate-800" : "bg-slate-100 text-slate-800"
                }`}>
                  <div className="text-[10px] uppercase tracking-wide font-semibold mb-1 opacity-60">
                    {t.role === "candidate" ? "Candidate" : "Interviewer"}
                  </div>
                  <div className="whitespace-pre-line">{t.text}</div>
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-slate-500">No transcript recorded.</div>}
      </Section>
    </div>
  );
}

const PROCTOR_DELAY_MS = 10 * 60 * 1000; // 10 minutes

function ProctoringSection({ proctoring, completedAt }) {
  const [now, setNow] = React.useState(Date.now());

  const completed = completedAt ? new Date(completedAt).getTime() : 0;
  const elapsed   = now - completed;
  const ready     = elapsed >= PROCTOR_DELAY_MS;
  const remaining = Math.max(0, PROCTOR_DELAY_MS - elapsed);
  const mins      = Math.floor(remaining / 60000);
  const secs      = Math.floor((remaining % 60000) / 1000);

  // Tick every second while still in the delay window
  React.useEffect(() => {
    if (ready) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ready]);

  if (!proctoring && ready) return null;   // no data and delay passed — nothing to show
  if (!proctoring && !ready) return null;  // no data yet at all — nothing to show

  if (!ready) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-violet-400" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Proctoring Report</h2>
        </div>
        <div className="flex items-center gap-3 bg-violet-50 border border-violet-100 rounded-lg px-4 py-3">
          <Loader2 className="w-4 h-4 text-violet-400 animate-spin flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-violet-700">Processing proctoring data…</div>
            <div className="text-xs text-violet-500 mt-0.5">
              Available in {mins}m {secs.toString().padStart(2, "0")}s — proctoring analysis runs after a 10-minute review window to ensure it does not affect immediate scoring.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <ProctoringReport proctoring={proctoring} />;
}

const RISK_STYLE = {
  low:    { bar: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Low risk"  },
  medium: { bar: "bg-amber-500",   badge: "bg-amber-100   text-amber-800   border-amber-300",   label: "Med risk"  },
  high:   { bar: "bg-rose-500",    badge: "bg-rose-100    text-rose-800    border-rose-300",     label: "High risk" },
};
const SEV_CLR = {
  high:   "text-rose-600   bg-rose-50   border-rose-100",
  medium: "text-amber-600  bg-amber-50  border-amber-100",
  low:    "text-slate-600  bg-slate-50  border-slate-100",
};

function ProctoringReport({ proctoring: p }) {
  const [showViolations, setShowViolations] = React.useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = React.useState(null);
  const style = RISK_STYLE[p.riskLevel] || RISK_STYLE.low;
  const score = Math.max(0, Math.min(100, p.integrityScore ?? 100));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-4 h-4 text-violet-500" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Proctoring Report</h2>
      </div>

      {/* Score + risk */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Integrity Score</span>
            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${style.badge}`}>
              {score}/100 · {style.label}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded">
            <div className={`h-2 rounded transition-all ${style.bar}`} style={{ width: `${score}%` }} />
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Total violations</div>
          <div className={`text-2xl font-black ${p.totalViolations > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {p.totalViolations}
          </div>
        </div>
      </div>

      {/* Event counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Tab switches",     value: p.browserEvents?.tabSwitches    || 0 },
          { label: "Focus losses",     value: p.browserEvents?.focusLosses    || 0 },
          { label: "No face detected", value: p.cameraEvents?.noFaceCount     || 0 },
          { label: "Multiple faces",   value: p.cameraEvents?.multiFaceCount  || 0 },
          { label: "Noise events",     value: p.audioEvents?.noiseCount       || 0 },
          { label: "External voices",  value: p.audioEvents?.externalVoiceCount || 0 },
          { label: "Fullscreen exits", value: p.browserEvents?.fullscreenExits || 0 },
          { label: "Window minimize",  value: p.browserEvents?.windowMinimizes || 0 },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
            <div className={`text-lg font-black ${value > 0 ? "text-rose-600" : "text-slate-400"}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Violation timeline */}
      {(p.violations || []).length > 0 && (
        <div>
          <button
            onClick={() => setShowViolations(v => !v)}
            className="text-xs font-semibold text-violet-600 hover:underline flex items-center gap-1 mb-2"
          >
            {showViolations ? "Hide" : "Show"} violation timeline ({p.violations.length})
          </button>
          {showViolations && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {p.violations.map((v, i) => (
                <div key={i} className={`flex items-start gap-2 text-xs border rounded px-3 py-2 ${SEV_CLR[v.severity] || SEV_CLR.low}`}>
                  <span className="font-bold capitalize mt-0.5 flex-shrink-0">{(v.type || "").replace(/_/g, " ")}</span>
                  <span className="flex-1">{v.description}</span>
                  <span className="text-slate-400 ml-2 flex-shrink-0">{v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ""}</span>
                  {v.screenshot && (
                    <button
                      onClick={() => setSelectedScreenshot(v.screenshot)}
                      className="ml-1 text-violet-500 hover:underline flex-shrink-0"
                    >
                      <Camera className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Screenshot lightbox */}
      {selectedScreenshot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setSelectedScreenshot(null)}
        >
          <div className="bg-white rounded-xl p-4 max-w-xs shadow-2xl">
            <div className="text-xs text-slate-500 mb-2 text-center">Violation screenshot</div>
            <img src={selectedScreenshot} alt="violation" className="rounded border border-slate-200 w-full" />
            <button
              className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700"
              onClick={() => setSelectedScreenshot(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, iconCls, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className={`w-4 h-4 ${iconCls || "text-slate-500"}`} />}
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{title}</h2>
      </div>
      {children}
    </div>
  );
}
